/**
 * Returns full face transforms, including a mirrored right eye, so the Avatar
 * component stays a dumb presenter.
 */

import { useMemo, useCallback } from 'react';
import {
  useRoomPlayers,
  useRoomInteractionWindow,
  useRoomConfig,
  useRoomTeams,
} from '../../../hooks';
import { expressionForScore, expressionForTeamScore } from '../utilities';
import type {
  ShapeExpression,
  UseAvatarExpressionProps,
  UseAvatarExpressionReturn,
} from '../types';
import { getAvailableShapeNames } from '@airhead-games/shared/configs';
import {
  getRandomBaseShape,
  getEmotionFaceTransforms,
} from '../configs/expressionTransforms';
import { getBaseShape } from '@airhead-games/shared/avatar';

/**
 * Default fallback shape names when nothing else is available
 */
const DEFAULT_SHAPES = {
  eye: 'arch', // Default eye shape from EYE_SHAPES
  mouth: 'boat', // Default mouth shape from MOUTH_SHAPES
} as const;

/**
 * Special mouth shapes for Poppy
 */
const POPPY_DEFAULT_MOUTH = 'circle-small'; // Poppy always shows circle-small by default
const POPPY_HOVER_MOUTH = 'circle-large'; // Poppy shows circle-large on hover

/**
 * Hook for avatar expression selection logic (updated for shape-based system)
 *
 * Determines which eye and mouth shapes should be displayed and calculates
 * their transforms based on emotion. Handles special cases like hover states
 * and maintains expression consistency during reactions.
 *
 * @param props - Configuration for expression selection
 * @returns Selected shapes with transforms and their source
 */
export function useAvatarExpression({
  player,
  emotion = 'happy',
  allowWaitingEmotionOverride = true,
  customExpression,
  isHovered = false,
  isReacting = false,
  isPoppy = false,
  reactionTransforms,
  scoreFace = 'off',
}: UseAvatarExpressionProps): UseAvatarExpressionReturn {
  // Room state for intelligent emotion resolution
  const { players } = useRoomPlayers();
  const { windowStatus, windowWaitingFor } = useRoomInteractionWindow();
  const { gameConfig } = useRoomConfig();
  const { teams } = useRoomTeams();

  // Calculate leaderboard emotion mapping
  const leaderboardEmotionMap = useMemo(() => {
    const enabled = scoreFace !== 'off';
    if (!enabled || players.length === 0) {
      return new Map();
    }
    const scoreField = scoreFace === 'turn' ? 'scoreTurn' : 'scoreGame';

    // Auto-detect team games and use team-based scoring
    if (gameConfig?.isTeamGame && teams.length > 0) {
      return expressionForTeamScore(teams, players, scoreField);
    }

    // Individual game scoring
    return expressionForScore(players, scoreField);
  }, [scoreFace, players, gameConfig?.isTeamGame, teams]);

  // Resolve final emotion with overlay priority:
  // 1) Leaderboard overlay
  // 2) Waiting player overlay
  // 3) Base emotion prop
  const resolvedEmotion = useMemo(() => {
    if (!player) return emotion;

    // Score-based faces take highest priority when enabled
    const leaderboardEmotion =
      scoreFace !== 'off' ? leaderboardEmotionMap.get(player.playerId) : null;
    if (leaderboardEmotion) {
      return leaderboardEmotion;
    }

    // Eliminated player overlay
    if (player.isEliminated) {
      return 'dead';
    }

    // Waiting player overlay is enabled by default for gameplay surfaces.
    // Selectors can opt out so explicit row emotions stay stable.
    if (
      allowWaitingEmotionOverride &&
      windowStatus === 'opened' &&
      windowWaitingFor.includes(player.playerId)
    ) {
      return 'nervous';
    }

    // Base emotion fallback
    return emotion;
  }, [
    player,
    emotion,
    scoreFace,
    leaderboardEmotionMap,
    allowWaitingEmotionOverride,
    windowStatus,
    windowWaitingFor,
  ]);
  // Deterministic picker for stable randomization per player+emotion
  const seededPick = (options: string[], seed: string) => {
    if (!options || options.length === 0) return '';
    // Simple string hash -> 32-bit integer
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
      h ^= seed.charCodeAt(i);
      h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
    }
    const idx = Math.abs(h) % options.length;
    return options[idx];
  };

  // Helper to create a ShapeExpression from shape name
  const createShapeExpression = useCallback(
    (shapeName: string, feature: 'eye' | 'mouth'): ShapeExpression => {
      const baseShape = getBaseShape(feature, shapeName);
      const path = baseShape?.path || '';

      // Get emotion transforms merged with reaction transforms
      const transforms = getEmotionFaceTransforms(
        resolvedEmotion,
        reactionTransforms
      );
      let transform = feature === 'eye' ? transforms.leftEye : transforms.mouth;

      // Poppy's mouth should always use scale 1.0 to prevent emotion transforms from making it too large
      if (isPoppy && feature === 'mouth') {
        transform = {
          ...transform,
          scale: 1.0,
        };
      }

      return {
        shapeName,
        path,
        transform,
      };
    },
    [resolvedEmotion, reactionTransforms, isPoppy]
  );

  // Base shape selection (stable; independent of hover/reacting)
  const baseShapes = useMemo(() => {
    let eyeShape = '';
    let mouthShape = '';
    let source: 'custom' | 'player' | 'random' = 'random';

    if (customExpression) {
      eyeShape = customExpression.eyeShape || '';
      mouthShape = customExpression.mouthShape || '';
      source = 'custom';
    } else if (player?.face) {
      // With single face schema, all emotions use the same base shapes
      if (player.face.eyeShape && player.face.mouthShape) {
        eyeShape = player.face.eyeShape as string;
        mouthShape = player.face.mouthShape as string;
        source = 'player';
      }
    }

    if (!eyeShape || !mouthShape) {
      // Random fallback; prefer seeded selection when player available
      if (player?.playerId) {
        const eyeShapes = getAvailableShapeNames('eye');
        const mouthShapes = getAvailableShapeNames('mouth');
        const seedEyes = `${player.playerId}:${resolvedEmotion}:eyes`;
        const seedMouth = `${player.playerId}:${resolvedEmotion}:mouth`;
        eyeShape = eyeShape || seededPick(eyeShapes, seedEyes);
        mouthShape = mouthShape || seededPick(mouthShapes, seedMouth);
      } else {
        const rndEye = getRandomBaseShape('eye');
        const rndMouth = getRandomBaseShape('mouth');
        eyeShape = eyeShape || rndEye.name;
        mouthShape = mouthShape || rndMouth.name;
      }
      source = source === 'player' || source === 'custom' ? source : 'random';
    }

    // Override eye shape for dead emotion
    const finalEyeShape =
      resolvedEmotion === 'dead' ? 'cross' : eyeShape || DEFAULT_SHAPES.eye;

    return {
      eyeShape: finalEyeShape,
      mouthShape: mouthShape || DEFAULT_SHAPES.mouth,
      source,
    } as const;
  }, [customExpression, player?.face, player?.playerId, resolvedEmotion]);

  // Apply hover-only overlay without re-randomizing
  // Poppy always uses its own mouth defaults, ignoring baseShapes.mouthShape
  const finalMouthShape = useMemo(() => {
    if (isPoppy) {
      // Allow customExpression to override Poppy's mouth (for music discovery player controls)
      if (customExpression?.mouthShape) return customExpression.mouthShape;
      // Poppy always shows circle-small by default, circle-large on hover
      if (isHovered && !isReacting) return POPPY_HOVER_MOUTH;
      return POPPY_DEFAULT_MOUTH;
    }
    return baseShapes.mouthShape;
  }, [
    isPoppy,
    isHovered,
    isReacting,
    customExpression?.mouthShape,
    baseShapes.mouthShape,
  ]);

  // Create final shape expressions with transforms
  const expressions = useMemo(() => {
    const eyeExpr = createShapeExpression(baseShapes.eyeShape, 'eye');
    const mouthExpr = createShapeExpression(finalMouthShape, 'mouth');

    // Mirror right eye transforms from left eye here so Avatar remains a dumb renderer
    const left = eyeExpr.transform;
    const faceTransforms = {
      leftEye: left,
      rightEye: {
        shiftX: -left.shiftX, // mirror X
        shiftY: left.shiftY, // keep Y
        scale: left.scale, // keep scale
        rotation: -left.rotation, // mirror rotation
      },
      mouth: mouthExpr.transform,
    } as const;

    return {
      eyeExpression: eyeExpr,
      mouthExpression: mouthExpr,
      expressionSource: baseShapes.source,
      faceTransforms,
    } as const;
  }, [
    baseShapes.eyeShape,
    baseShapes.source,
    finalMouthShape,
    createShapeExpression,
  ]);

  return {
    ...expressions,
    resolvedEmotion,
  } as UseAvatarExpressionReturn;
}
